import { useTranslation } from 'react-i18next';
import { Disclaimer, LabeledComponent, ObsoleteRangePicker } from '@commonClient2/components';
import { applyUkgUserTimeZoneTransformation, formatUTCDateAsIs, getDateTimeFormats } from '@commonClient2/services/ukgDateTime';
import { BidProfileStatus, UkgDateFormat } from '../../../Enums';
import {
    Fields, isForever, isValidDate, roundDatePickerValues
} from './SchedulePeriod.service';
import DateFormatService from '../../../services/DateFormatService';

const SchedulePeriodPreferential = ({
    profile,
    requestPeriod,
    requestPeriodMinDate,
    requestPeriodMaxDate,
    validationErrors,
    handleDateRangeChange,
}) => {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'form' });
    const userDateTimeFormats = getDateTimeFormats();
    const now = [applyUkgUserTimeZoneTransformation(new Date()), applyUkgUserTimeZoneTransformation(new Date())];

    const {
        schedule,
        isCreationAllowed,
        isViewAllowed,
        status,
    } = profile;

    const {
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        isEditable
    } = schedule;
    
    const defaultSubmissionPeriodValues = roundDatePickerValues(Fields.SubmissionPeriod, now);
    const defaultRequestPeriodValues = roundDatePickerValues(Fields.RequestPeriod, requestPeriod);

    const rangesSubmission = [
        submissionPeriodStartDate || defaultSubmissionPeriodValues[0],
        submissionPeriodEndDate || defaultSubmissionPeriodValues[1],
    ];

    const rangesRequest = [
        isValidDate(requestPeriodStartDate) || isValidDate(requestPeriodMinDate) || defaultRequestPeriodValues[0],
        isValidDate(requestPeriodEndDate) || isValidDate(requestPeriodMaxDate) || defaultRequestPeriodValues[1],
    ];

    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isInputDisabled = !isEditable || hasOnlyViewAccess;
    const isActiveBid = status === BidProfileStatus.Active;
    
    return (
        <div className="schedule-period-content">
            {isActiveBid ? (
                <LabeledComponent name={t('schedulePeriod.submissionPeriod')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="read-only-date">
                            {DateFormatService.formatDateOrEmpty(submissionPeriodStartDate, UkgDateFormat.LongDateTime)}
                        </span>
                        <span>-</span>
                        <ObsoleteRangePicker
                            onChange={(updatedValue) => handleDateRangeChange(Fields.SubmissionPeriod, [rangesSubmission[0], updatedValue[0]])}
                            onExit={(_event, picker) => handleDateRangeChange(Fields.SubmissionPeriod, [rangesSubmission[0], picker.startDate.toDate()])}
                            value={[rangesSubmission[1], rangesSubmission[1]]}
                            minDate={rangesSubmission[0]}
                            singleDatePicker
                            placement="up"
                            disabled={isInputDisabled}
                            timePicker
                            timePickerIncrement={5}
                            applyButtonClasses="date-range-picker-apply-button"
                            locale={{
                                applyLabel: t('button.dateRangePickerApplyLabel'),
                                cancelLabel: t('button.dateRangePickerCancelLabel'),
                            }}
                        />
                    </div>
                </LabeledComponent>
            ) : (
                <LabeledComponent name={t('schedulePeriod.submissionPeriod')}>
                    <ObsoleteRangePicker
                        onChange={(updatedValue) => handleDateRangeChange(Fields.SubmissionPeriod, updatedValue)}
                        onExit={(_event, picker) => handleDateRangeChange(Fields.SubmissionPeriod, [picker.startDate, picker.endDate])}
                        value={rangesSubmission}
                        minDate={applyUkgUserTimeZoneTransformation(new Date())}
                        placement="up"
                        disabled={isInputDisabled}
                        timePicker
                        timePickerIncrement={5}
                        applyButtonClasses="date-range-picker-apply-button"
                        locale={{
                            applyLabel: t('button.dateRangePickerApplyLabel'),
                            cancelLabel: t('button.dateRangePickerCancelLabel'),
                        }}
                    />
                </LabeledComponent>
            )}
            {
                (validationErrors.submissionPeriod)
                    ? <span style={{ color: 'red' }}>{t('error.submissionPeriodWarning')}</span>
                    : null
            }
            {
                profile.originalScheduleTemplates.isActiveWorkplanBid ? (
                    <LabeledComponent name={t('schedulePeriod.requestPeriodStartDate')}>
                        <ObsoleteRangePicker
                            onChange={(updatedValue) =>
                                handleDateRangeChange(Fields.RequestPeriod, [updatedValue[0].toDate(), rangesRequest[1]])}
                            onExit={(_event, picker) =>
                                handleDateRangeChange(Fields.RequestPeriod, [picker.startDate.toDate(), rangesRequest[1]])}
                            value={rangesRequest}
                            singleDatePicker
                            minDate={requestPeriodMinDate}
                            maxDate={requestPeriodMaxDate || false}
                            placement="up"
                            disabled={isInputDisabled}
                            applyButtonClasses="date-range-picker-apply-button"
                            locale={{
                                applyLabel: t('button.dateRangePickerApplyLabel'),
                                cancelLabel: t('button.dateRangePickerCancelLabel'),
                            }}
                        />
                    </LabeledComponent>
                ) : (
                    <LabeledComponent name={t('schedulePeriod.requestPeriod')}>
                        <ObsoleteRangePicker
                            onChange={(updatedValue) => handleDateRangeChange(Fields.RequestPeriod, updatedValue)}
                            onExit={(_event, picker) => handleDateRangeChange(Fields.RequestPeriod, [picker.startDate, picker.endDate])}
                            value={rangesRequest}
                            minDate={requestPeriodMinDate}
                            maxDate={requestPeriodMaxDate || false}
                            placement="up"
                            disabled={isInputDisabled}
                            applyButtonClasses="date-range-picker-apply-button"
                            // @ts-ignore
                            hasForeverEndDateButton={profile.originalRequestPeriodEndDateIsForever}
                            foreverDate={requestPeriodMaxDate}
                            foreverLabel={t('activeWorkplanDates.forever')}
                            locale={{
                                applyLabel: t('button.dateRangePickerApplyLabel'),
                                cancelLabel: t('button.dateRangePickerCancelLabel'),
                            }}
                        />
                    </LabeledComponent>
                )
            }
            {
                (validationErrors.requestPeriod)
                    ? <span style={{ color: 'red' }}>{t('error.requestPeriodWarning')}</span>
                    : null
            }
            {
                (validationErrors.submissionBeforeRequest)
                    ? <span style={{ color: 'red' }}>{t('error.submissionBeforeRequest')}</span>
                    : null
            }
            {
                profile.originalScheduleTemplates.isActiveWorkplanBid ? (
                    <div>
                        <Disclaimer className="workplan-period-disclaimer">
                            {
                                t(
                                    'activeWorkplanDates.disclaimer',
                                    {
                                        startDate: formatUTCDateAsIs(
                                            // @ts-ignore
                                            new Date(profile.originalScheduleTemplates.activeWorkplanStartDate * 1000),
                                            userDateTimeFormats.ShortDateFormat,
                                        ),
                                        endDate: (
                                            isForever(profile.originalScheduleTemplates.activeWorkplanEndDate)
                                                ? t('activeWorkplanDates.forever')
                                                : formatUTCDateAsIs(
                                                    // @ts-ignore
                                                    new Date(profile.originalScheduleTemplates.activeWorkplanEndDate * 1000),
                                                    userDateTimeFormats.ShortDateFormat,
                                                )
                                        ),
                                    },
                                )
                            }
                        </Disclaimer>
                        <Disclaimer className="workplan-period-disclaimer">
                            {t('activeWorkplanDates.disclaimer2')}
                        </Disclaimer>
                    </div>
                ) : null
            }
        </div>
    );
};

export default SchedulePeriodPreferential;
